import { useEffect, useMemo, useState } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Upload, Trash2, Edit, Download, X, ArrowUpDown, Truck, FileSpreadsheet, FileType2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { PageLoader } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { WhatsAppChatIconButton } from "@/components/WhatsAppShare";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

const SORT_OPTIONS = [
  { value: "nombre-asc", label: "Nombre A → Z" },
  { value: "nombre-desc", label: "Nombre Z → A" },
  { value: "pais-asc", label: "País de origen A → Z" },
  { value: "categoria-asc", label: "Categoría A → Z" },
  { value: "recientes", label: "Más recientes primero" },
  { value: "antiguos", label: "Más antiguos primero" },
];

const emptyForm = {
  nombre: "",
  nombre_contacto: "",
  email: "",
  telefono: "",
  pais_origen: "",
  categorias: [],
  tipo_compra: "inventario",
  tipo_oferta: "productos",
  metodo_pago: "",
  banco: "",
  cuenta_iban: "",
  cuenta_numero: "",
  cuenta_titular: "",
  sinpe_numero: "",
  sinpe_titular: "",
  moneda_pago: "CRC",
  notas_pago: "",
};

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [csvPreviewData, setCsvPreviewData] = useState([]);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [categoriaInput, setCategoriaInput] = useState("");
  const [sortBy, setSortBy] = useState("nombre-asc");

  const sortedSuppliers = useMemo(() => {
    const list = [...suppliers];
    const cmp = (a, b) => (a || "").toString().localeCompare((b || "").toString(), "es", { sensitivity: "base" });
    switch (sortBy) {
      case "nombre-desc": return list.sort((a, b) => cmp(b.nombre, a.nombre));
      case "pais-asc": return list.sort((a, b) => cmp(a.pais_origen, b.pais_origen));
      case "categoria-asc": return list.sort((a, b) => cmp((a.categorias || []).join(","), (b.categorias || []).join(",")));
      case "recientes": return list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      case "antiguos": return list.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      case "nombre-asc":
      default: return list.sort((a, b) => cmp(a.nombre, b.nombre));
    }
  }, [suppliers, sortBy]);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await api.get("/suppliers");
      setSuppliers(response.data);
    } catch (error) {
      toast.error("Error al cargar proveedores");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        await api.put(`/suppliers/${editingSupplier.id}`, { ...formData, id: editingSupplier.id });
        toast.success("Proveedor actualizado");
      } else {
        await api.post("/suppliers", formData);
        toast.success("Proveedor creado");
      }
      setIsDialogOpen(false);
      setEditingSupplier(null);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error("Error al guardar proveedor");
    }
  };

  const handleDelete = (id) => {
    setDeleteTarget({ type: 'single', id });
    setDeleteConfirmOpen(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      toast.error("No hay proveedores seleccionados");
      return;
    }
    setDeleteTarget({ type: 'bulk' });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (deleteTarget?.type === 'single') {
        await api.delete(`/suppliers/${deleteTarget.id}`);
        toast.success("Proveedor eliminado");
      } else if (deleteTarget?.type === 'bulk') {
        const response = await api.post('/suppliers/bulk-delete', selectedIds);
        toast.success(response.data.message);
        setSelectedIds([]);
      }
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
      fetchSuppliers();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === suppliers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(suppliers.map(s => s.id));
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      nombre: supplier.nombre || "",
      nombre_contacto: supplier.nombre_contacto || "",
      email: supplier.email || "",
      telefono: supplier.telefono || "",
      pais_origen: supplier.pais_origen || "",
      categorias: supplier.categorias || [],
      tipo_compra: supplier.tipo_compra || "inventario",
      tipo_oferta: supplier.tipo_oferta || "productos",
      metodo_pago: supplier.metodo_pago || "",
      banco: supplier.banco || "",
      cuenta_iban: supplier.cuenta_iban || "",
      cuenta_numero: supplier.cuenta_numero || "",
      cuenta_titular: supplier.cuenta_titular || "",
      sinpe_numero: supplier.sinpe_numero || "",
      sinpe_titular: supplier.sinpe_titular || "",
      moneda_pago: supplier.moneda_pago || "CRC",
      notas_pago: supplier.notas_pago || "",
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setCategoriaInput("");
  };

  const addCategoria = () => {
    if (!categoriaInput.trim()) return;
    if (formData.categorias.includes(categoriaInput.trim())) {
      toast.error("Esta categoría ya está agregada");
      return;
    }
    setFormData({
      ...formData,
      categorias: [...formData.categorias, categoriaInput.trim()]
    });
    setCategoriaInput("");
  };

  const removeCategoria = (index) => {
    setFormData({
      ...formData,
      categorias: formData.categorias.filter((_, i) => i !== index)
    });
  };

  const downloadTemplate = async (format = "csv") => {
    try {
      const token = localStorage.getItem("token");
      const url = format === "excel"
        ? `${process.env.REACT_APP_BACKEND_URL}/api/suppliers/template/excel`
        : `${process.env.REACT_APP_BACKEND_URL}/api/suppliers/template`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Failed to download");
      const blob = await response.blob();
      const objUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objUrl;
      link.setAttribute('download', format === "excel" ? 'plantilla_proveedores.xlsx' : 'plantilla_proveedores.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objUrl);
      toast.success(`Plantilla ${format === "excel" ? "Excel" : "CSV"} descargada`);
    } catch (error) {
      toast.error("Error al descargar plantilla");
    }
  };

  const exportSuppliers = async (format = "csv") => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/suppliers/export/${format}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("export failed");
      const blob = await response.blob();
      const ext = format === "excel" ? "xlsx" : "csv";
      const objUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objUrl;
      link.setAttribute('download', `proveedores.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objUrl);
      toast.success(`Proveedores exportados (${ext.toUpperCase()})`);
    } catch {
      toast.error("Error al exportar proveedores");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isExcel = /\.(xlsx|xls)$/i.test(file.name);
    if (isExcel) {
      // Excel can't be parsed in the browser cheaply, skip preview and
      // upload directly. The backend reads both CSV and XLSX.
      try {
        const formData = new FormData();
        formData.append("file", file);
        const response = await api.post("/suppliers/import", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toast.success(response.data.message);
        fetchSuppliers();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Error al importar proveedores");
      }
      e.target.value = '';
      return;
    }

    try {
      let text = await file.text();
      if (text.charCodeAt(0) === 0xFEFF) {
        text = text.slice(1);
      }

      const parseCSVLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        result.push(current.trim());
        return result;
      };

      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) {
        toast.error("El CSV está vacío o no tiene datos");
        return;
      }

      const headers = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());

      const previewData = [];
      for (let i = 1; i < Math.min(lines.length, 11); i++) {
        const values = parseCSVLine(lines[i]);
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        previewData.push(row);
      }

      window._supplierCsvFile = file;
      setCsvPreviewData(previewData);
      setIsPreviewDialogOpen(true);
    } catch (error) {
      toast.error("Error al leer el archivo CSV");
    }

    e.target.value = '';
  };

  const finalizarImportacion = async () => {
    if (!window._supplierCsvFile) {
      toast.error("No hay archivo para importar");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", window._supplierCsvFile);

      const response = await api.post("/suppliers/import", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(response.data.message);
      setIsPreviewDialogOpen(false);
      setCsvPreviewData([]);
      window._supplierCsvFile = null;
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al importar proveedores");
    }
  };

  if (loading) {
    return <PageLoader label="Cargando proveedores…" testid="suppliers-loader" />;
  }

  return (
    <div data-testid="suppliers-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Proveedores
          </h2>
          <p className="text-slate-500 text-sm mt-1">Gestiona tus proveedores</p>
        </div>
        <div className="flex gap-3">
          {selectedIds.length > 0 && (
            <Button
              onClick={handleBulkDelete}
              variant="outline"
              className="rounded-sm text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              data-testid="bulk-delete-suppliers-button"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar ({selectedIds.length})
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-sm" data-testid="download-suppliers-template-button">
                <Download className="h-4 w-4 mr-2" />
                Plantilla
                <ChevronDown className="h-3 w-3 ml-1.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm">
              <DropdownMenuItem onClick={() => downloadTemplate("excel")} className="rounded-sm cursor-pointer" data-testid="download-suppliers-template-excel">
                <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadTemplate("csv")} className="rounded-sm cursor-pointer" data-testid="download-suppliers-template-csv">
                <FileType2 className="h-4 w-4 mr-2 text-slate-600" /> CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <label>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="import-suppliers-input"
            />
            <Button
              type="button"
              onClick={(e) => e.currentTarget.previousElementSibling.click()}
              variant="outline"
              className="rounded-sm"
              data-testid="import-suppliers-button"
            >
              <Upload className="h-4 w-4 mr-2" />
              Importar
            </Button>
          </label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-sm" data-testid="export-suppliers-button">
                <Download className="h-4 w-4 mr-2" />
                Exportar
                <ChevronDown className="h-3 w-3 ml-1.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm">
              <DropdownMenuItem onClick={() => exportSuppliers("excel")} className="rounded-sm cursor-pointer" data-testid="export-suppliers-excel">
                <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportSuppliers("csv")} className="rounded-sm cursor-pointer" data-testid="export-suppliers-csv">
                <FileType2 className="h-4 w-4 mr-2 text-slate-600" /> CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setEditingSupplier(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary rounded-sm" data-testid="add-supplier-button">
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Proveedor
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingSupplier ? "Editar Proveedor" : "Nuevo Proveedor"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1.5">Nombre *</label>
                    <Input
                      data-testid="supplier-name-input"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      required
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Nombre del Contacto</label>
                    <Input
                      data-testid="supplier-contact-input"
                      value={formData.nombre_contacto}
                      onChange={(e) => setFormData({ ...formData, nombre_contacto: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">País de Origen</label>
                    <Input
                      data-testid="supplier-country-input"
                      value={formData.pais_origen}
                      onChange={(e) => setFormData({ ...formData, pais_origen: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1.5">Email</label>
                    <Input
                      data-testid="supplier-email-input"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1.5">Teléfono</label>
                    <Input
                      data-testid="supplier-phone-input"
                      value={formData.telefono}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                      className="rounded-sm"
                      placeholder="+506 8888-8888"
                    />
                  </div>
                </div>

                {/* Clasificación */}
                <div className="border border-slate-200 rounded-sm p-4 bg-slate-50" data-testid="supplier-classification-section">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-sm bg-[rgb(0,47,167)] text-white text-[10px] font-bold">1</span>
                    Clasificación del proveedor
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1.5">Tipo de compra</label>
                      <Select
                        value={formData.tipo_compra}
                        onValueChange={(v) => setFormData({ ...formData, tipo_compra: v })}
                      >
                        <SelectTrigger className="rounded-sm" data-testid="supplier-tipo-compra-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="inventario">Inventario (mercadería para vender)</SelectItem>
                          <SelectItem value="operativo">Operativo (gastos del negocio)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-slate-500 mt-1">
                        {formData.tipo_compra === "inventario"
                          ? "Productos que ingresan al inventario y se revenden."
                          : "Insumos y servicios que el negocio consume para operar."}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1.5">¿Qué te vende?</label>
                      <Select
                        value={formData.tipo_oferta}
                        onValueChange={(v) => setFormData({ ...formData, tipo_oferta: v })}
                      >
                        <SelectTrigger className="rounded-sm" data-testid="supplier-tipo-oferta-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="productos">Productos físicos</SelectItem>
                          <SelectItem value="servicios">Servicios</SelectItem>
                          <SelectItem value="ambos">Productos y servicios</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Datos de pago */}
                <div className="border border-slate-200 rounded-sm p-4" data-testid="supplier-payment-section">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-sm bg-[rgb(0,47,167)] text-white text-[10px] font-bold">2</span>
                    Datos de pago habituales
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1.5">Método de pago</label>
                      <Select
                        value={formData.metodo_pago || "__none__"}
                        onValueChange={(v) => setFormData({ ...formData, metodo_pago: v === "__none__" ? "" : v })}
                      >
                        <SelectTrigger className="rounded-sm" data-testid="supplier-metodo-pago-select">
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Sin definir —</SelectItem>
                          <SelectItem value="transferencia">Transferencia bancaria</SelectItem>
                          <SelectItem value="sinpe_movil">SINPE Móvil</SelectItem>
                          <SelectItem value="efectivo">Efectivo</SelectItem>
                          <SelectItem value="cheque">Cheque</SelectItem>
                          <SelectItem value="tarjeta">Tarjeta</SelectItem>
                          <SelectItem value="otro">Otro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1.5">Moneda</label>
                      <Select
                        value={formData.moneda_pago}
                        onValueChange={(v) => setFormData({ ...formData, moneda_pago: v })}
                      >
                        <SelectTrigger className="rounded-sm" data-testid="supplier-moneda-pago-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CRC">Colones (CRC ₡)</SelectItem>
                          <SelectItem value="USD">Dólares (USD $)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Datos bancarios — visible para transferencia o cheque (cuenta opcional siempre util) */}
                    {(formData.metodo_pago === "transferencia" || formData.metodo_pago === "cheque" || formData.metodo_pago === "otro" || formData.metodo_pago === "") && (
                      <>
                        <div>
                          <label className="block text-xs font-medium mb-1">Banco</label>
                          <Input
                            data-testid="supplier-banco-input"
                            value={formData.banco}
                            onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                            className="rounded-sm h-9"
                            placeholder="BAC, BCR, BN, Davivienda…"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Titular de la cuenta</label>
                          <Input
                            data-testid="supplier-cuenta-titular-input"
                            value={formData.cuenta_titular}
                            onChange={(e) => setFormData({ ...formData, cuenta_titular: e.target.value })}
                            className="rounded-sm h-9"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">IBAN</label>
                          <Input
                            data-testid="supplier-iban-input"
                            value={formData.cuenta_iban}
                            onChange={(e) => setFormData({ ...formData, cuenta_iban: e.target.value })}
                            className="rounded-sm h-9 mono"
                            placeholder="CR05015202001026284066"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">N° de cuenta cliente</label>
                          <Input
                            data-testid="supplier-cuenta-numero-input"
                            value={formData.cuenta_numero}
                            onChange={(e) => setFormData({ ...formData, cuenta_numero: e.target.value })}
                            className="rounded-sm h-9 mono"
                          />
                        </div>
                      </>
                    )}

                    {/* SINPE Móvil */}
                    {(formData.metodo_pago === "sinpe_movil" || formData.metodo_pago === "otro" || formData.metodo_pago === "") && (
                      <>
                        <div>
                          <label className="block text-xs font-medium mb-1">Número SINPE Móvil</label>
                          <Input
                            data-testid="supplier-sinpe-numero-input"
                            value={formData.sinpe_numero}
                            onChange={(e) => setFormData({ ...formData, sinpe_numero: e.target.value })}
                            className="rounded-sm h-9 mono"
                            placeholder="8888-8888"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Nombre del monedero SINPE</label>
                          <Input
                            data-testid="supplier-sinpe-titular-input"
                            value={formData.sinpe_titular}
                            onChange={(e) => setFormData({ ...formData, sinpe_titular: e.target.value })}
                            className="rounded-sm h-9"
                            placeholder="Nombre tal como aparece en SINPE"
                          />
                        </div>
                      </>
                    )}

                    <div className="col-span-2">
                      <label className="block text-xs font-medium mb-1">Notas de pago (opcional)</label>
                      <Input
                        data-testid="supplier-notas-pago-input"
                        value={formData.notas_pago}
                        onChange={(e) => setFormData({ ...formData, notas_pago: e.target.value })}
                        className="rounded-sm h-9"
                        placeholder="Crédito 30 días, depósito en oficina, etc."
                      />
                    </div>
                  </div>
                </div>

                <div className="border border-slate-200 rounded-sm p-4">
                  <h4 className="font-medium mb-3">Categorías</h4>
                  <div className="flex gap-2 mb-3">
                    <Input
                      data-testid="category-input"
                      placeholder="Agregar categoría (ej: Telas, Empaques)"
                      value={categoriaInput}
                      onChange={(e) => setCategoriaInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addCategoria();
                        }
                      }}
                      className="rounded-sm flex-1"
                    />
                    <Button
                      type="button"
                      onClick={addCategoria}
                      className="btn-primary rounded-sm"
                      data-testid="add-category-button"
                    >
                      Agregar
                    </Button>
                  </div>

                  {formData.categorias.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
                      {formData.categorias.map((cat, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-sm text-sm"
                        >
                          <span>{cat}</span>
                          <button
                            type="button"
                            onClick={() => removeCategoria(index)}
                            className="text-slate-500 hover:text-red-600"
                            data-testid={`remove-category-${index}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className="rounded-sm"
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" className="btn-primary rounded-sm" data-testid="save-supplier-button">
                    {editingSupplier ? "Actualizar" : "Crear"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Vista Previa de Importación CSV</DialogTitle>
              </DialogHeader>
              <div className="mt-4">
                {csvPreviewData.length > 0 ? (
                  <>
                    <p className="text-sm text-slate-600 mb-4">
                      Se importarán {csvPreviewData.length} proveedores. Revisa los datos antes de finalizar.
                    </p>
                    <div className="border border-slate-200 rounded-sm overflow-x-auto max-h-96">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2 px-3 font-medium text-slate-600">Nombre</th>
                            <th className="text-left py-2 px-3 font-medium text-slate-600">Contacto</th>
                            <th className="text-left py-2 px-3 font-medium text-slate-600">Email</th>
                            <th className="text-left py-2 px-3 font-medium text-slate-600">País</th>
                            <th className="text-left py-2 px-3 font-medium text-slate-600">Categorías</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvPreviewData.map((row, index) => (
                            <tr key={index} className="border-b border-slate-100">
                              <td className="py-2 px-3">{row.nombre || '-'}</td>
                              <td className="py-2 px-3">{row.nombre_contacto || row.contacto || '-'}</td>
                              <td className="py-2 px-3">{row.email || '-'}</td>
                              <td className="py-2 px-3">{row.pais_origen || row.pais || '-'}</td>
                              <td className="py-2 px-3">{row.categorias || row.categorías || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setIsPreviewDialogOpen(false);
                          setCsvPreviewData([]);
                          window._supplierCsvFile = null;
                        }}
                        className="rounded-sm"
                      >
                        Cancelar
                      </Button>
                      <Button
                        onClick={finalizarImportacion}
                        className="btn-primary rounded-sm"
                        data-testid="finalize-suppliers-import-button"
                      >
                        Finalizar Importación
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-center py-8 text-slate-500">No hay datos para previsualizar</p>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="bg-white border border-slate-200 rounded-sm p-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <ArrowUpDown className="h-4 w-4 text-slate-500" />
          <span className="text-xs font-medium text-slate-700 uppercase tracking-wider">Ordenar por:</span>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="rounded-sm h-8 text-xs w-60" data-testid="suppliers-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-700 ml-auto">{sortedSuppliers.length} proveedor{sortedSuppliers.length === 1 ? "" : "es"}</span>
        </div>
      </Card>

      <Card className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {sortedSuppliers.length === 0 ? (
          <EmptyState
            icon={Truck}
            title={suppliers.length === 0 ? "Aún no hay proveedores" : "Sin coincidencias"}
            description={
              suppliers.length === 0
                ? "Registra tu primer proveedor o importa tu lista desde CSV para gestionar compras y pagos."
                : "No encontramos proveedores con esos filtros. Probá ajustando la búsqueda."
            }
            actionLabel={suppliers.length === 0 ? "Nuevo proveedor" : undefined}
            actionOnClick={suppliers.length === 0 ? () => { resetForm(); setIsDialogOpen(true); } : undefined}
            testid="suppliers-empty-state"
          />
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="py-3 px-4 w-12">
                  <Checkbox
                    checked={sortedSuppliers.length > 0 && selectedIds.length === sortedSuppliers.length}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-suppliers-checkbox"
                  />
                </th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">Nombre</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">Tipo</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">Contacto</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">País</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">Pago</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">Categorías</th>
                <th className="text-right py-3 px-4 font-medium text-slate-700">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {sortedSuppliers.map((supplier) => (
                  <tr
                    key={supplier.id}
                    className="table-row border-b border-slate-100 cursor-pointer"
                    data-testid="supplier-row"
                    onClick={() => handleEdit(supplier)}
                  >
                    <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.includes(supplier.id)}
                        onCheckedChange={() => toggleSelect(supplier.id)}
                        data-testid={`select-supplier-${supplier.id}`}
                      />
                    </td>
                    <td className="py-3 px-4 font-medium">{supplier.nombre}</td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm border w-fit ${
                          (supplier.tipo_compra || "inventario") === "inventario"
                            ? "bg-blue-50 text-[rgb(0,47,167)] border-blue-200"
                            : "bg-amber-50 text-amber-700 border-amber-200"
                        }`} data-testid={`supplier-tipo-compra-${supplier.id}`}>
                          {(supplier.tipo_compra || "inventario") === "inventario" ? "Inventario" : "Operativo"}
                        </span>
                        <span className="text-[10px] text-slate-500 lowercase" data-testid={`supplier-tipo-oferta-${supplier.id}`}>
                          {(supplier.tipo_oferta || "productos") === "ambos"
                            ? "productos + servicios"
                            : supplier.tipo_oferta || "productos"}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col">
                        <span>{supplier.nombre_contacto || "-"}</span>
                        {supplier.telefono && (
                          <span className="text-[11px] text-slate-500 inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            {supplier.telefono}
                            <WhatsAppChatIconButton phone={supplier.telefono} testid={`supplier-wa-${supplier.id}`} />
                          </span>
                        )}
                        {supplier.email && <span className="text-[11px] text-slate-500 truncate max-w-[180px]">{supplier.email}</span>}
                      </div>
                    </td>
                    <td className="py-3 px-4">{supplier.pais_origen || '-'}</td>
                    <td className="py-3 px-4" data-testid={`supplier-pago-cell-${supplier.id}`}>
                      {supplier.metodo_pago ? (
                        <div className="flex flex-col">
                          <span className="text-xs font-medium text-slate-800">
                            {supplier.metodo_pago === "transferencia" && "Transferencia"}
                            {supplier.metodo_pago === "sinpe_movil" && "SINPE Móvil"}
                            {supplier.metodo_pago === "efectivo" && "Efectivo"}
                            {supplier.metodo_pago === "cheque" && "Cheque"}
                            {supplier.metodo_pago === "tarjeta" && "Tarjeta"}
                            {supplier.metodo_pago === "otro" && "Otro"}
                            {" "}
                            <span className="text-[10px] text-slate-500">({supplier.moneda_pago || "CRC"})</span>
                          </span>
                          {supplier.metodo_pago === "sinpe_movil" && supplier.sinpe_numero && (
                            <span className="text-[10px] text-slate-500 mono">{supplier.sinpe_numero}</span>
                          )}
                          {(supplier.metodo_pago === "transferencia" || supplier.metodo_pago === "cheque") && supplier.banco && (
                            <span className="text-[10px] text-slate-500">{supplier.banco}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {supplier.categorias && supplier.categorias.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {supplier.categorias.slice(0, 3).map((cat, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 bg-slate-100 rounded-sm">
                              {cat}
                            </span>
                          ))}
                          {supplier.categorias.length > 3 && (
                            <span className="text-xs text-slate-500">+{supplier.categorias.length - 3}</span>
                          )}
                        </div>
                      ) : '-'}
                    </td>
                    <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-2">
                        <Button
                          data-testid={`edit-supplier-${supplier.id}`}
                          onClick={() => handleEdit(supplier)}
                          variant="ghost"
                          size="sm"
                          className="rounded-sm"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          data-testid={`delete-supplier-${supplier.id}`}
                          onClick={() => handleDelete(supplier.id)}
                          variant="ghost"
                          size="sm"
                          className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        )}
      </Card>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Confirmar eliminación?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === 'bulk'
                ? `Estás a punto de eliminar ${selectedIds.length} proveedor(es). Esta acción no se puede deshacer.`
                : 'Estás a punto de eliminar este proveedor. Esta acción no se puede deshacer.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-supplier-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-supplier-button"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
